import { J2Multiply, J2Theme } from '@e3dc-react/icons'
import { Upload } from '@e3dc-react/shell/Elements/FileTransfer'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import useImages from '../../hooks/use-images'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationStatementType } from 'src/Libs/installation-statement/models/images'
import { useFormikContext } from 'formik'

interface ImagesProps {
  deviceLinkId: number
  installationId: number
  imageType: InstallationStatementType
  previewUrl?: string
  fileId?: number
  required: boolean
  combinedFileSize: number
}

const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg']
const ACCEPTED_FILE_EXTENSIONS = ['.png', '.jpg', '.jpeg']

export const useStyles = makeStyles()(theme => ({
  imagePreviewContainer: {
    position: 'relative',
    borderStyle: 'solid',
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.grey.grey03,
    borderWidth: '1px',
    backgroundColor: theme.palette.background.paper2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    height: '100%',
    minHeight: 120,
    justifyContent: 'center',
  },
  imagePreview: {
    borderRadius: theme.shape.borderRadius / 2,
    maxHeight: 80,
    maxWidth: 80,
    boxShadow: theme.effectStyles.shadowStrong,
  },
  imagePreviewTitle: {
    padding: theme.spacing(2),
  },
  imageUpload: {
    height: '100%',
    minHeight: 120,
  },
  deleteWrapper: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  imageUploadContent: {
    color: theme.palette.primary.main,
    padding: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}))

const MAX_FILE_SIZE = 15 * 1000 * 1000
const MAX_OVERALL_SIZE = 100 * 1000 * 1000
export const INSTALLATION_STATEMENT_IMAGE_NAME = 'installation-statement-image'

/**
 * Component for the installation statement wizard images step
 */
const ImageUpload: React.FC<ImagesProps> = ({ deviceLinkId, installationId, imageType, previewUrl, fileId, required, combinedFileSize }) => {
  const { classes } = useStyles()
  const { uploadImage, deleteImage, getImagePreview } = useImages()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const { setFieldValue } = useFormikContext()
  const [status, setStatus] = useState<'loading' | 'empty' | 'uploaded'>(previewUrl ? 'uploaded' : 'empty')
  const fieldName = `images.${imageType}`

  const startImageUpload = (file: Blob): void => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      enqueueErrorStack(t('installation-statement.images.unallowedType'))
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      enqueueErrorStack(t('installation-statement.images.file-too-large'))
      return
    }

    if (file.size + combinedFileSize > MAX_OVERALL_SIZE) {
      enqueueErrorStack(t('installation-statement.images.combined-file-size-too-large'))
      return
    }

    setStatus('loading')

    uploadImage(deviceLinkId, installationId, file, imageType)
      .then(fileId => {
        if (!fileId) {
          enqueueErrorStack(t('installation-statement.images.upload-error'))
          setStatus('empty')
          return
        }

        getImagePreview(installationId, fileId).then(previewUrl => {
          if (!previewUrl) {
            setStatus('empty')
            return
          }

          setFieldValue(fieldName, {
            id: fileId,
            required,
            name: `${INSTALLATION_STATEMENT_IMAGE_NAME}-${imageType}`,
            previewUrl,
            size: file.size,
          })
          setStatus('uploaded')
        })
      })
      .catch(error => {
        enqueueErrorStack(t('installation-statement.images.upload-error'))
        console.error(error)
        setStatus('empty')
      })
  }

  const generateLabel = (): string => {
    const labelName = t(`installation-statement.images.types.${imageType}`)
    const optional = t('installation-statement.images.optional')

    if (required) {
      return `${labelName}*`
    } else {
      return `(${optional}) ${labelName}`
    }
  }
  const removeImage = (): void => {
    if (!fileId) {
      return
    }

    setStatus('loading')
    deleteImage(installationId, fileId).then(success => {
      if (success) {
        setFieldValue(fieldName, {
          id: undefined,
          required,
          name: `${INSTALLATION_STATEMENT_IMAGE_NAME}-${imageType}`,
          previewUrl: undefined,
          size: undefined,
        })
        setStatus('empty')
      } else {
        enqueueErrorStack(t('installation-statement.images.delete-error'))
        setStatus('uploaded')
      }
    })
  }

  if (status === 'loading') {
    return (
      <Box className={classes.imagePreviewContainer}>
        <Box className={classes.imageUploadContent}>
          <CircularProgress />
          <Typography variant="h3">{generateLabel()}</Typography>
        </Box>
      </Box>
    )
  } else if (status === 'uploaded') {
    return (
      <Box className={classes.imagePreviewContainer}>
        <Box className={classes.deleteWrapper}>
          <IconButton onClick={removeImage}>
            <J2Multiply />
          </IconButton>
        </Box>
        <img alt={'preview'} src={previewUrl} className={classes.imagePreview}></img>
        <Typography className={classes.imagePreviewTitle}>{t(`installation-statement.images.types.${imageType}`)}</Typography>
      </Box>
    )
  } else {
    return (
      <Upload
        className={classes.imageUpload}
        content={
          <Box className={classes.imageUploadContent}>
            <J2Theme fontSize="large" />
            <Typography variant="h3">{generateLabel()}</Typography>
          </Box>
        }
        onFilesDropped={startImageUpload}
        oneFile={true}
        showFile={false}
        acceptedFileTypes={ACCEPTED_FILE_EXTENSIONS}
      />
    )
  }
}

export default ImageUpload
